import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/Button";
import DiagnosisCard from "./DiagnosisCard";
import { diagnosePatientSentry, getPatientSentry } from "./sentryApi";
import { formatUtc, isoToUtcInput, utcInputToIso } from "./sentryActivityUtils";

const WINDOW_SOURCE_LABELS = {
  pipeline: "around the first pipeline run",
  appointment: "appointment day",
  custom: "custom",
  recent: "last 24 hours",
};

const PROBLEM_LOGS = [
  "Recording discarded",
  "Recording permission denied",
  "Recording paused: empty first chunk",
  "Recording stopped due to invalid chunks",
];

const IDLE_RUN = { status: "idle", result: null, error: null, inputsKey: null };

const ErrorNote = ({ children }) => (
  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{children}</p>
);

const shortTime = (value) => formatUtc(value).replace(" UTC", "");

const logsKey = (data) =>
  JSON.stringify({
    start: data?.window?.start || null,
    end: data?.window?.end || null,
    logs: (data?.logs || []).map((log) => [log.timestamp, log.message, log.patient_id]),
  });

const LogList = ({ logs, patientId }) =>
  logs.length === 0 ? (
    <p className="text-sm text-gray-500">
      No app logs for this doctor in the window. That doesn't prove nothing happened (retention or a blocked network
      can hide them).
    </p>
  ) : (
    <ul className="space-y-0.5 text-sm">
      {logs.map((log, index) => {
        const isThisPatient = String(log.patient_id) === String(patientId);
        const isProblem = log.severity === "warn" || log.severity === "error" || PROBLEM_LOGS.includes(log.message);
        return (
          <li key={index} className={`flex flex-wrap items-baseline gap-x-2 rounded px-1 ${isThisPatient ? "bg-purple-50" : ""}`}>
            <span className="text-xs text-gray-500 font-mono">{shortTime(log.timestamp)}</span>
            <span className={isProblem ? "text-red-700 font-medium" : "text-gray-800"}>{log.message}</span>
            {log.patient_id && (
              <span className={`text-xs ${isThisPatient ? "text-purple-800 font-medium" : "text-gray-500"}`}>
                /record/{log.patient_id}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );

const MissingNotesAction = ({ patient, refreshKey = 0 }) => {
  const patientId = patient?.patient_id ? String(patient.patient_id) : null;
  const [customWindow, setCustomWindow] = useState(null);
  const [windowDraft, setWindowDraft] = useState(null);
  const [sentry, setSentry] = useState({ loading: true, data: null, error: null });
  const [run, setRun] = useState(IDLE_RUN);
  const diagnoseController = useRef(null);

  useEffect(() => {
    setCustomWindow(null);
    setWindowDraft(null);
    diagnoseController.current?.abort();
    setRun(IDLE_RUN);
  }, [patientId]);

  useEffect(() => () => diagnoseController.current?.abort(), []);

  useEffect(() => {
    if (!patientId) return undefined;
    const controller = new AbortController();
    setSentry((prev) => ({ ...prev, loading: true, error: null }));
    getPatientSentry(patientId, { ...customWindow, signal: controller.signal }).then((data) => {
      if (data.aborted) return;
      setSentry(data.error ? { loading: false, data: null, error: data.error } : { loading: false, data, error: null });
    });
    return () => controller.abort();
  }, [patientId, refreshKey, customWindow]);

  if (!patientId) return null;

  const data = sentry.data;
  const logs = data?.logs || [];
  const currentKey = data ? logsKey(data) : null;
  const isStale = run.status === "done" && currentKey !== null && currentKey !== run.inputsKey;

  const handleDiagnose = async () => {
    diagnoseController.current?.abort();
    const controller = new AbortController();
    diagnoseController.current = controller;
    const inputsKey = currentKey;
    setRun({ ...IDLE_RUN, status: "running" });

    const outcome = await diagnosePatientSentry(patientId, {
      start: data?.window?.start,
      end: data?.window?.end,
      signal: controller.signal,
    });
    if (outcome.aborted || diagnoseController.current !== controller) return;
    setRun(
      outcome.error
        ? { ...IDLE_RUN, status: "error", error: outcome.error }
        : { ...IDLE_RUN, status: "done", result: outcome, inputsKey }
    );
  };

  const applyWindow = () => {
    const start = utcInputToIso(windowDraft?.start);
    const end = utcInputToIso(windowDraft?.end);
    if (start && end) setCustomWindow({ start, end });
    setWindowDraft(null);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-600">The doctor's web app logs from Sentry around this visit.</p>

      {data?.window && !windowDraft && (
        <p className="text-sm text-gray-700">
          Window: {formatUtc(data.window.start)} – {formatUtc(data.window.end)}{" "}
          <span className="text-gray-500">
            ({WINDOW_SOURCE_LABELS[data.window.source] || data.window.source}
            {data.window.clamped && ", start limited to what you can access"})
          </span>
          {data.environment && <span className="text-gray-500"> · Sentry {data.environment}</span>}{" "}
          <button
            type="button"
            className="text-ktx-purple hover:underline"
            onClick={() => setWindowDraft({ start: isoToUtcInput(data.window.start), end: isoToUtcInput(data.window.end) })}
          >
            Edit
          </button>
          {customWindow && (
            <>
              {" · "}
              <button type="button" className="text-ktx-purple hover:underline" onClick={() => setCustomWindow(null)}>
                Reset
              </button>
            </>
          )}
        </p>
      )}
      {windowDraft && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-1">
            From
            <input
              type="datetime-local"
              value={windowDraft.start}
              onChange={(e) => setWindowDraft((prev) => ({ ...prev, start: e.target.value }))}
              className="border border-gray-300 rounded px-1.5 py-0.5"
            />
          </label>
          <label className="flex items-center gap-1">
            to
            <input
              type="datetime-local"
              value={windowDraft.end}
              onChange={(e) => setWindowDraft((prev) => ({ ...prev, end: e.target.value }))}
              className="border border-gray-300 rounded px-1.5 py-0.5"
            />
            UTC
          </label>
          <button type="button" className="text-ktx-purple hover:underline font-medium" onClick={applyWindow}>
            Apply
          </button>
          <button type="button" className="text-gray-500 hover:underline" onClick={() => setWindowDraft(null)}>
            Cancel
          </button>
        </div>
      )}

      {sentry.error && <ErrorNote>{sentry.error}</ErrorNote>}

      {sentry.loading && !data ? (
        <div className="space-y-2" aria-label="Loading Sentry logs">
          {[0, 1, 2].map((row) => (
            <div key={row} className="h-6 rounded bg-gray-200 animate-pulse" />
          ))}
        </div>
      ) : (
        data && (
          <div className={`space-y-2 ${sentry.loading ? "opacity-60" : ""}`}>
            <h5 className="text-sm font-semibold text-gray-900">Sentry logs</h5>
            {data.errors?.logs ? (
              <ErrorNote>Logs: {data.errors.logs}</ErrorNote>
            ) : (
              <>
                <LogList logs={logs} patientId={patientId} />
                {data.truncated?.logs && (
                  <p className="text-xs text-amber-700">Only the first page is shown. Narrow the window to see the rest.</p>
                )}
              </>
            )}
          </div>
        )
      )}

      <div className="border-t border-gray-200 pt-4 space-y-3">
        {run.status === "running" ? (
          <p className="flex items-center gap-2 text-sm font-medium text-gray-700" aria-live="polite">
            <Loader2 className="h-4 w-4 animate-spin text-ktx-purple" aria-hidden />
            Checking the logs and replays…
          </p>
        ) : (
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={handleDiagnose}
              disabled={sentry.loading || !data}
              className="bg-ktx-purple hover:bg-purple-700 text-white"
            >
              {run.status === "done" ? "Re-run" : "Diagnose"}
            </Button>
            <span className="text-xs text-gray-500">
              The AI reads the doctor's Sentry logs and replays and tells you whether they hit record for this
              patient.
            </span>
          </div>
        )}
        {run.status === "error" && <ErrorNote>{run.error}</ErrorNote>}
        {run.status === "done" && <DiagnosisCard result={run.result} isStale={isStale} />}
      </div>
    </div>
  );
};

export default MissingNotesAction;
